import { createJurisdictionHeaders } from '@/data';
import { URLS } from '@/data/config';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { PlayHistoryRange } from './use-play-history';

export type WalletType = 'fantasy' | 'social';

export type WalletHistoryStats = {
    amount_deposited?: number;
    amount_withdrawn?: number;
    amount_purchased?: number;
    betr_cash_awarded?: number;
    betr_cash_redeemed?: number;
    amount_bonused?: number;
};

type WalletTypesResponse = {
    data: { wallet_types: WalletType[] };
};

type WalletHistoryResponse = {
    data: WalletHistoryStats;
};

const getWalletTypes = async (): Promise<WalletType[]> => {
    const resp = await fetch(`${URLS.CHAMELON_API_URL}/auth/wallet/wallet-types`, {
        method: 'GET',
        headers: createJurisdictionHeaders(),
    });
    const data: WalletTypesResponse = await resp.json();
    if (!resp.ok) {
        throw data;
    }
    return data.data?.wallet_types ?? [];
};

const getWalletHistory = async (
    wallet: WalletType,
    { fromYear, fromMonth, toYear, toMonth }: PlayHistoryRange
): Promise<WalletHistoryStats> => {
    const params = new URLSearchParams({
        wallet,
        year_from: `${fromYear}`,
        month_from: `${fromMonth}`,
        year_to: `${toYear}`,
        month_to: `${toMonth}`,
    });
    const resp = await fetch(`${URLS.CHAMELON_API_URL}/auth/wallet/wallet-history?${params}`, {
        method: 'GET',
        headers: createJurisdictionHeaders(),
    });
    const data: WalletHistoryResponse = await resp.json();
    if (!resp.ok) {
        throw data;
    }
    return data.data;
};

/**
 * Returns the wallet types the user has activity in.
 * Used to list the wallets on the Account Summary screen.
 * */
export const useWalletTypes = () => {
    const { data = [], ...query } = useQuery({
        queryKey: ['wallet-types'],
        queryFn: getWalletTypes,
    });

    return { data, ...query };
};

/**
 * Returns the wallet history stats for the given wallet and range.
 * Defaults to the current calendar year.
 * */
export const useWalletHistory = (wallet: WalletType, range?: Partial<PlayHistoryRange>) => {
    const currentYear = new Date().getFullYear();
    const params: PlayHistoryRange = {
        fromYear: range?.fromYear ?? currentYear,
        fromMonth: range?.fromMonth ?? 1,
        toYear: range?.toYear ?? currentYear,
        toMonth: range?.toMonth ?? 12,
    };

    const { data, ...query } = useQuery({
        queryKey: ['wallet-history', wallet, params],
        queryFn: () => getWalletHistory(wallet, params),
        placeholderData: keepPreviousData,
    });

    return { data, ...query };
};
